//codex-- Preserve the dashboard month (including All) in shareable map URLs, with safe defaults for direct visits.
export function getContractMapPeriod(params, now = new Date()) {
  const month = params.get('month');
  const year = params.get('year');
  return {
    month: month === 'all' || /^(0[1-9]|1[0-2])$/.test(month || '') ? month : String(now.getMonth() + 1).padStart(2, '0'),
    year: /^\d{4}$/.test(year || '') && Number(year) >= 1900 ? year : String(now.getFullYear()),
  };
}

//codex-- Geocode the street/locality only; apartment and other address2 notes remain in contract details.
export function contractMapAddress(contract) {
  const details = contract.contract_details || {};
  return [details.job_site_address, details.job_site_city, [details.job_site_state, details.job_site_zip].filter(Boolean).join(' ')]
    .map(value => String(value || '').trim()).filter(Boolean).join(', ');
}

//codex-- Account for every due contract and request each normalized street address only once.
export function groupContractMapAddresses(contracts) {
  const groups = new Map();
  const unmapped = [];
  contracts.forEach(contract => {
    const details = contract.contract_details || {};
    const address = contractMapAddress(contract);
    if (!String(details.job_site_address || '').trim() || !String(details.job_site_city || details.job_site_zip || '').trim()) {
      unmapped.push({ contract, reason: 'A street address and city or ZIP are required.' });
      return;
    }
    const key = address.replace(/\s+/g, ' ').toLowerCase();
    if (!groups.has(key)) groups.set(key, { key, address, contracts: [] });
    groups.get(key).contracts.push(contract);
  });
  return { groups: [...groups.values()], unmapped };
}

//codex-- Resolve addresses sequentially with bounded transient retries, cancellation and in-memory success caching.
export async function resolveContractMapAddresses(groups, geocode, {
  cache = new Map(), isCancelled = () => false, onProgress = () => {},
  wait = ms => new Promise(resolve => setTimeout(resolve, ms)),
} = {}) {
  const pins = new Map();
  const unmapped = [];
  let completed = 0;
  let serviceError = '';
  for (const group of groups) {
    if (isCancelled()) return null;
    let position = cache.get(group.key);
    let reason = serviceError;
    if (!position && !reason) {
      let response;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (isCancelled()) return null;
        try { response = await geocode(group.address); }
        catch { response = { status: 'UNKNOWN_ERROR', results: [] }; }
        if (isCancelled()) return null;
        if (!['OVER_QUERY_LIMIT', 'UNKNOWN_ERROR'].includes(response.status) || attempt === 2) break;
        await wait(300 * (attempt + 1));
      }
      const result = response.results?.[0];
      if (response.status === 'OK' && result) {
        const location = result.geometry?.location;
        const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
        const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;
        if (result.partial_match || ['APPROXIMATE', 'GEOMETRIC_CENTER'].includes(result.geometry?.location_type)) {
          reason = 'Only an approximate address match was found. Check the job-site address.';
        } else if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
          position = { lat, lng };
          cache.set(group.key, position);
        } else reason = 'No usable map coordinates were returned.';
      } else if (response.status === 'ZERO_RESULTS') reason = 'Address could not be located. Check the job-site address.';
      else if (['REQUEST_DENIED', 'OVER_DAILY_LIMIT', 'OVER_QUERY_LIMIT'].includes(response.status)) {
        serviceError = response.status === 'REQUEST_DENIED'
          ? 'Address lookup was denied. Check the Google Maps key and enabled APIs.'
          : 'Address lookup quota was reached. Try again later.';
        reason = serviceError;
      } else reason = 'Address lookup failed. Try refreshing the map.';
    }
    if (position) {
      //codex-- Merge different address strings resolving to one point so overlapping pins never hide contracts.
      const key = `${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
      if (!pins.has(key)) pins.set(key, { key, position, address: group.address, contracts: [] });
      pins.get(key).contracts.push(...group.contracts);
    } else unmapped.push(...group.contracts.map(contract => ({ contract, reason })));
    completed++;
    onProgress(completed);
  }
  return isCancelled() ? null : { locations: [...pins.values()], unmapped, serviceError };
}
